import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneIcon from '@mui/icons-material/Phone';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import { useAuth } from '../contexts/AuthContext';
import { useConnectivity } from '../contexts/ConnectivityContext';
import { useEventSystemData } from './eventSystem/EventSystemProviderFixed';
import { useAssistantOptional } from '../assistant/AssistantContext';
import AssistantButton from '../assistant/AssistantButton';
import NotificationCenter from './notifications/NotificationCenter';
import CallPanel from '../pages/HomeLauncher/components/CallPanel';
import notificationStore from '../stores/notificationStore';
import { getDealerContexts, getTenantAgentsHealth } from '../api/backendApi';
import { logOut } from '../services/authService';
import { acceptCall } from '../services/voice/voiceCallService';
import { ROUTES } from '../utils/constants';
import { openCallPanel, requestAddHomeIcon } from '../utils/chromeEvents';
import { useDealerAdmin } from '../contexts/DealerAdminContext';
import { getTopChromeActions } from '../utils/topChromeActions';
import { useAlgoDeviceSupport } from '../pages/Admin/algoDeviceSupport';
import {
  WORKSPACE_NARROW_CHROME_HEIGHT_PX,
  WORKSPACE_RAIL_WIDTH_PX,
  applyWorkspaceRailCssVars,
  clearWorkspaceRailCssVars,
  readViewportWidth,
  workspaceCanvasOffset,
} from '../utils/workspaceRail';
import {
  agentRailStatus,
  summarizeTenantAgentsHealth,
} from '../utils/agentHealth';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const iconBtnSx = {
  width: 28,
  height: 28,
  color: '#4d4d4d',
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
};

function ActiveMark({ active, narrow }) {
  if (!active) return null;
  return (
    <Box
      aria-hidden="true"
      data-selection-indicator="bar"
      sx={{
        position: 'absolute',
        bgcolor: '#fff',
        borderRadius: '2px',
        ...(narrow
          ? { left: 8, right: 8, bottom: 3, height: 3 }
          : { top: 8, bottom: 8, left: 3, width: 3 }),
      }}
    />
  );
}

function railButtonSx(active, narrow) {
  return {
    width: 40,
    height: 40,
    color: '#f9fafb',
    borderRadius: '10px',
    position: 'relative',
    flexShrink: 0,
    boxShadow: active
      ? (narrow ? 'inset 0 -3px 0 #ffffff' : 'inset 3px 0 0 #ffffff')
      : 'none',
    backgroundColor: active ? 'rgba(255,255,255,0.16)' : 'transparent',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
    '&:focus-visible': { outline: '2px solid #ffffff', outlineOffset: 2 },
  };
}

function formatNow(ts) {
  const d = ts instanceof Date ? ts : new Date(ts);
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).replace(',', ' •');
}

export default function TopControlBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, role, currentUser, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const algoDeviceSupport = useAlgoDeviceSupport();
  const assistant = useAssistantOptional();
  useEventSystemData();
  const { isOffline, isOnline, forceOffline, forceOnline } = useConnectivity();
  const [powerLoading, setPowerLoading] = useState(false);
  const [hasDealerAccess, setHasDealerAccess] = useState(Boolean(dealerAdmin.active));

  useEffect(() => {
    let cancelled = false;
    if (dealerAdmin.active) {
      setHasDealerAccess(true);
      return undefined;
    }
    getDealerContexts()
      .then((response) => {
        const rows = Array.isArray(response?.data) ? response.data : [];
        if (!cancelled) setHasDealerAccess(rows.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasDealerAccess(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealerAdmin.active]);

  const actions = useMemo(
    () => getTopChromeActions({
      pathname: location.pathname,
      role,
      assistantEnabled: Boolean(assistant?.status?.enabled),
      dealerActive: dealerAdmin.active,
      hasDealerAccess,
      dealerId: dealerAdmin.dealerId,
      tenantId: dealerAdmin.tenantId,
      permissions: userData?.permissions,
      algoDeviceSupport,
    }),
    [
      algoDeviceSupport,
      assistant?.status?.enabled,
      dealerAdmin.active,
      dealerAdmin.dealerId,
      dealerAdmin.tenantId,
      hasDealerAccess,
      location.pathname,
      role,
      userData?.permissions,
    ]
  );

  const [now, setNow] = useState(new Date());
  const [message, setMessage] = useState('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [agentStatus, setAgentStatus] = useState('UNKNOWN');
  const [runtimeActivating, setRuntimeActivating] = useState(false);
  const [incomingCallInfo, setIncomingCallInfo] = useState(null);
  const [pendingNotifications, setPendingNotifications] = useState(
    () => notificationStore.getPendingCount()
  );
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);

  useEffect(() => {
    const sync = () => setPendingNotifications(notificationStore.getPendingCount());
    sync();
    return notificationStore.subscribe(sync);
  }, []);

  const loadAgentStatus = useCallback(async (force = false) => {
    const localStatus = window?.electronAPI?.getLocalAgentStatus;
    if (typeof localStatus === 'function') {
      try {
        const local = await localStatus();
        setAgentStatus(String(local?.state || 'status_unavailable').toUpperCase());
      } catch (error) {
        console.error('[TopControlBar] Failed to load local agent status', error);
        setAgentStatus('STATUS_UNAVAILABLE');
      }
      return;
    }

    if (!tenantId) {
      setAgentStatus('UNKNOWN');
      return;
    }

    try {
      const resp = await getTenantAgentsHealth(String(tenantId), force ? { maxAgeMs: 0 } : { maxAgeMs: 55000 });
      const agents = Array.isArray(resp?.data?.agents) ? resp.data.agents : [];
      setAgentStatus(summarizeTenantAgentsHealth(agents).overall);
    } catch (error) {
      console.error('[TopControlBar] Failed to load agent status', error);
      setAgentStatus('ERROR');
    }
  }, [tenantId]);

  useEffect(() => {
    if (!actions.visible || !actions.showAgent) return undefined;
    loadAgentStatus();
    const timer = setInterval(loadAgentStatus, window?.electronAPI?.getLocalAgentStatus ? 15000 : 60000);
    return () => clearInterval(timer);
  }, [actions.showAgent, actions.visible, loadAgentStatus]);

  useEffect(() => {
    const tick = () => setNow(new Date());
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      const detail = event?.detail;
      const next = typeof detail === 'string' ? detail : detail?.message;
      setMessage(String(next || '').trim());
    };

    window.addEventListener('bottom-bar-message', onMessage);
    return () => window.removeEventListener('bottom-bar-message', onMessage);
  }, []);

  useEffect(() => {
    const offlineNotice = 'Agent Offline • System running in offline mode';
    if (isOffline && !message) {
      setMessage(offlineNotice);
      return;
    }
    if (!isOffline && message === offlineNotice) {
      setMessage('');
    }
  }, [isOffline, message]);

  useEffect(() => {
    const onIncomingCall = (event) => {
      const detail = event?.detail || {};
      setIncomingCallInfo({
        from: String(detail.from || '').trim() || 'Unknown',
        to: String(detail.to || '').trim() || 'Unknown',
      });
    };

    const clearIncomingCall = () => {
      setIncomingCallInfo(null);
    };

    window.addEventListener('voice-incoming-call', onIncomingCall);
    window.addEventListener('voice-call-active', clearIncomingCall);
    window.addEventListener('voice-call-ended', clearIncomingCall);

    return () => {
      window.removeEventListener('voice-incoming-call', onIncomingCall);
      window.removeEventListener('voice-call-active', clearIncomingCall);
      window.removeEventListener('voice-call-ended', clearIncomingCall);
    };
  }, []);

  const agentPresentation = useMemo(
    () => agentRailStatus(agentStatus),
    [agentStatus]
  );

  const monthTitle = useMemo(
    () => calendarMonth.toLocaleDateString([], { month: 'long', year: 'numeric' }),
    [calendarMonth]
  );

  const calendarCells = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < firstDay; i += 1) {
      cells.push({ key: `empty-start-${i}`, day: null });
    }

    const today = new Date();
    for (let day = 1; day <= daysInMonth; day += 1) {
      const isToday =
        year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
      cells.push({ key: `day-${day}`, day, isToday });
    }

    while (cells.length % 7 !== 0) {
      cells.push({ key: `empty-end-${cells.length}`, day: null });
    }

    return cells;
  }, [calendarMonth]);

  const closeAccountMenu = () => setAccountMenuAnchor(null);

  const openAccountMenu = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleLogout = async () => {
    closeAccountMenu();
    try {
      await logOut();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handlePowerToggle = async () => {
    if (powerLoading) return;
    setPowerLoading(true);
    try {
      if (isOffline) {
        await forceOnline();
      } else {
        await forceOffline();
      }
    } finally {
      setPowerLoading(false);
    }
  };

  const handleActivateAgent = async () => {
    if (!window?.electronAPI?.activateLocalRuntimeAgent || runtimeActivating) return;
    try {
      setRuntimeActivating(true);
      await window.electronAPI.activateLocalRuntimeAgent();
      setTimeout(() => loadAgentStatus(true), 5000);
      setTimeout(() => loadAgentStatus(true), 15000);
    } catch (error) {
      console.error('[TopControlBar] Runtime activation failed', error);
    } finally {
      setRuntimeActivating(false);
    }
  };

  const [viewportWidth, setViewportWidth] = useState(readViewportWidth);
  const [railTip, setRailTip] = useState(null);
  const railTipTarget = useRef(null);
  const narrow = workspaceCanvasOffset(viewportWidth, true).paddingLeft === 0
    && workspaceCanvasOffset(viewportWidth, true).paddingTop === WORKSPACE_NARROW_CHROME_HEIGHT_PX;
  const settingsActive = actions.showSettings && (
    location.pathname === actions.settingsPath
    || location.pathname.startsWith(`${actions.settingsPath}/`)
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(readViewportWidth());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!currentUser || !actions.visible) {
      clearWorkspaceRailCssVars(root);
      return undefined;
    }
    applyWorkspaceRailCssVars(root, { viewportWidth, railVisible: true });
    return () => clearWorkspaceRailCssVars(root);
  }, [actions.visible, currentUser, viewportWidth]);

  const clearRailTip = () => {
    railTipTarget.current?.removeAttribute('aria-describedby');
    railTipTarget.current = null;
    setRailTip(null);
  };

  const showRailTip = (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    const item = target.closest('[data-tooltip]');
    if (!item || !event.currentTarget.contains(item)) return;
    const label = item.getAttribute('data-tooltip');
    if (!label) return;
    if (railTipTarget.current && railTipTarget.current !== item) {
      railTipTarget.current.removeAttribute('aria-describedby');
    }
    item.setAttribute('aria-describedby', 'workspace-rail-tooltip');
    railTipTarget.current = item;
    const rect = item.getBoundingClientRect();
    setRailTip({
      label,
      top: rect.bottom + 8,
      left: Math.max(8, rect.left),
    });
  };

  if (!currentUser || !actions.visible) return null;

  return (
    <>
      <Box
        component="nav"
        aria-label="Workspace"
        data-testid="top-control-bar"
        data-layout={narrow ? 'top' : 'rail'}
        onMouseOver={showRailTip}
        onMouseLeave={clearRailTip}
        onFocus={showRailTip}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) clearRailTip();
        }}
        sx={{
          position: 'fixed',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          background: incomingCallInfo ? '#7c2d12' : '#1f2937',
          color: '#f9fafb',
          boxShadow: '2px 0 8px rgba(0,0,0,0.18)',
          ...(narrow
            ? {
                top: 0,
                left: 0,
                right: 0,
                height: WORKSPACE_NARROW_CHROME_HEIGHT_PX,
                flexDirection: 'row',
                px: 1,
              }
            : {
                top: 0,
                left: 0,
                bottom: 0,
                width: WORKSPACE_RAIL_WIDTH_PX,
                flexDirection: 'column',
                py: 1,
              }),
        }}
      >
        <Stack
          direction={narrow ? 'row' : 'column'}
          spacing={0.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          {actions.showAdminHome ? (
            <Tooltip title="Admin" disableFocusListener disableHoverListener disableTouchListener>
              <IconButton
                size="small"
                data-testid="chrome-admin-home"
                data-nav-control="true"
                data-tooltip="Admin"
                aria-label="Admin home"
                onClick={() => navigate(actions.adminHomePath || ROUTES.ADMIN_FLOWS)}
                sx={railButtonSx(false, narrow)}
              >
                <img
                  src="/app-icon.png"
                  alt=""
                  width={18}
                  height={18}
                  style={{ width: 18, height: 18, objectFit: 'contain', display: 'block' }}
                />
              </IconButton>
            </Tooltip>
          ) : null}
          {actions.showOperatorHome ? (
            <Tooltip title="Home" disableFocusListener disableHoverListener disableTouchListener>
              <IconButton
                size="small"
                data-testid="chrome-operator-home"
                data-tooltip="Home"
                aria-label="Operator home"
                onClick={() => navigate(ROUTES.HOME_LAUNCHER)}
                sx={railButtonSx(false, narrow)}
              >
                <HomeIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={`Calendar · ${formatNow(now)}`} disableFocusListener disableHoverListener disableTouchListener>
            <IconButton
              size="small"
              data-testid="chrome-calendar"
              data-nav-control="true"
              data-tooltip={`Calendar · ${formatNow(now)}`}
              aria-label="Open calendar"
              aria-pressed={calendarOpen}
              data-active={calendarOpen ? 'true' : 'false'}
              onClick={() => setCalendarOpen((prev) => !prev)}
              sx={railButtonSx(calendarOpen, narrow)}
            >
              <CalendarMonthIcon sx={{ fontSize: '18px' }} />
              <ActiveMark active={calendarOpen} narrow={narrow} />
            </IconButton>
          </Tooltip>
          {actions.showAddIcon ? (
            <Tooltip title="Add button" disableFocusListener disableHoverListener disableTouchListener>
              <IconButton
                size="small"
                data-testid="chrome-add-icon"
                data-nav-control="true"
                data-tooltip="Add button"
                aria-label="Add button"
                onClick={() => requestAddHomeIcon()}
                sx={railButtonSx(false, narrow)}
              >
                <AddIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          ) : null}
          {actions.showPhone ? (
            <Tooltip title="Call panel" disableFocusListener disableHoverListener disableTouchListener>
              <Badge
                color="error"
                variant="dot"
                invisible={!incomingCallInfo}
                overlap="circular"
              >
                <IconButton
                  size="small"
                  data-testid="chrome-phone"
                  data-nav-control="true"
                  data-tooltip="Call panel"
                  aria-label="Open call panel"
                  aria-pressed={Boolean(incomingCallInfo)}
                  data-active={incomingCallInfo ? 'true' : 'false'}
                  onClick={() => openCallPanel()}
                  sx={{
                    ...railButtonSx(Boolean(incomingCallInfo), narrow),
                    color: incomingCallInfo ? '#fecaca' : '#f9fafb',
                  }}
                >
                  <PhoneIcon sx={{ fontSize: '18px' }} />
                  <ActiveMark active={Boolean(incomingCallInfo)} narrow={narrow} />
                </IconButton>
              </Badge>
            </Tooltip>
          ) : null}
          {actions.showAssistant ? (
            <AssistantButton />
          ) : null}
          <NotificationCenter
            buttonTestId="chrome-notifications"
            navControl
            buttonSx={{
              ...railButtonSx(false, narrow),
              color: '#f9fafb',
            }}
          />
          {pendingNotifications > 0 ? (
            <Tooltip
              title={`${pendingNotifications} notification${pendingNotifications === 1 ? '' : 's'} waiting for you — click to open`}
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <Chip
                size="small"
                clickable
                color="primary"
                icon={<NotificationsActiveIcon sx={{ fontSize: '14px' }} />}
                label={pendingNotifications}
                onClick={() => notificationStore.reopenPending()}
                data-testid="pending-notifications-chip"
                data-tooltip={`${pendingNotifications} notification${pendingNotifications === 1 ? '' : 's'} waiting for you — click to open`}
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  '& .MuiChip-label': { px: 0.75 },
                  '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
                }}
              />
            </Tooltip>
          ) : null}
          {actions.showTraining ? (
            <Tooltip title="Training" disableFocusListener disableHoverListener disableTouchListener>
              <IconButton
                size="small"
                data-testid="chrome-training"
                data-nav-control="true"
                data-tooltip="Training"
                aria-label="Open training"
                onClick={() => navigate(ROUTES.TRAINING)}
                sx={railButtonSx(false, narrow)}
              >
                <SchoolIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
        <Box data-testid="workspace-rail-spacer" sx={narrow ? { flex: 1 } : { flex: 1, width: '100%' }} />
        {actions.showAgent ? (
          <Tooltip title={agentPresentation.label} arrow disableFocusListener disableHoverListener disableTouchListener>
            <IconButton
              size="small"
              data-testid="agent-status"
              data-nav-control="true"
              data-tooltip={agentPresentation.label}
              aria-label={agentPresentation.label}
              onClick={handleActivateAgent}
              sx={railButtonSx(false, narrow)}
            >
              <img
                src={agentPresentation.src}
                alt=""
                width={18}
                height={18}
                style={{ width: 18, height: 18, objectFit: 'contain', display: 'block' }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
        {actions.showSettings ? (
          <Tooltip title={currentUser.displayName || 'Your account'} disableFocusListener disableHoverListener disableTouchListener>
            <IconButton
              size="small"
              data-testid="chrome-user"
              data-nav-control="true"
              data-tooltip={currentUser.displayName || 'Your account'}
              aria-label={currentUser.displayName || 'Your account'}
              onClick={openAccountMenu}
              sx={railButtonSx(false, narrow)}
            >
              {currentUser.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt=""
                  width={22}
                  height={22}
                  style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <AccountCircleIcon sx={{ fontSize: '22px' }} />
              )}
            </IconButton>
          </Tooltip>
        ) : null}
        {actions.showSettings ? (
          <Tooltip title="Settings" disableFocusListener disableHoverListener disableTouchListener>
            <IconButton
              size="small"
              data-testid="chrome-settings"
              data-nav-control="true"
              data-tooltip="Settings"
              aria-label="Settings"
              aria-pressed={Boolean(accountMenuAnchor)}
              aria-current={settingsActive ? 'page' : undefined}
              data-active={settingsActive || accountMenuAnchor ? 'true' : 'false'}
              onClick={openAccountMenu}
              sx={railButtonSx(settingsActive || Boolean(accountMenuAnchor), narrow)}
            >
              <SettingsIcon sx={{ fontSize: '18px' }} />
              <ActiveMark active={settingsActive || Boolean(accountMenuAnchor)} narrow={narrow} />
            </IconButton>
          </Tooltip>
        ) : null}
        {actions.showPower ? (
          <Tooltip title={isOffline ? 'Go Online' : 'Go Offline'} disableFocusListener disableHoverListener disableTouchListener>
            <IconButton
              size="small"
              data-testid="chrome-power"
              data-nav-control="true"
              data-tooltip={isOffline ? 'Go online' : 'Go offline'}
              aria-label={isOffline ? 'Go online' : 'Go offline'}
              onClick={handlePowerToggle}
              disabled={powerLoading}
              sx={{
                ...railButtonSx(false, narrow),
                color: isOnline ? '#86efac' : '#fbbf24',
              }}
            >
              <PowerSettingsNewIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={closeAccountMenu}
        anchorOrigin={narrow
          ? { vertical: 'bottom', horizontal: 'right' }
          : { vertical: 'center', horizontal: 'right' }}
        transformOrigin={narrow
          ? { vertical: 'top', horizontal: 'right' }
          : { vertical: 'center', horizontal: 'left' }}
        slotProps={{ paper: { 'data-testid': 'account-menu' } }}
      >
        <MenuItem
          data-testid="account-menu-reports"
          onClick={() => {
            closeAccountMenu();
            navigate(ROUTES.SUPPORT_REPORTS);
          }}
        >
          Support
        </MenuItem>
        <MenuItem
          data-testid="account-menu-home-settings"
          onClick={() => {
            closeAccountMenu();
            navigate(ROUTES.LAUNCHER_SETTINGS);
          }}
        >
          Home settings
        </MenuItem>
        <MenuItem data-testid="account-menu-logout" onClick={handleLogout}>
          Log out
        </MenuItem>
        <MenuItem
          data-testid="account-menu-login"
          onClick={() => {
            closeAccountMenu();
            navigate(ROUTES.LOGIN);
          }}
        >
          Log in
        </MenuItem>
      </Menu>
      {railTip ? (
        <Box
          id="workspace-rail-tooltip"
          role="tooltip"
          sx={{
            position: 'fixed',
            top: railTip.top,
            left: railTip.left,
            zIndex: 1400,
            bgcolor: '#111827',
            color: '#fff',
            px: 1,
            py: 0.5,
            borderRadius: '6px',
            fontSize: '0.75rem',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {railTip.label}
        </Box>
      ) : null}

      {incomingCallInfo || message ? (
        <Paper
          elevation={2}
          data-testid="workspace-status-banner"
          sx={{
            position: 'fixed',
            zIndex: 1298,
            top: narrow ? WORKSPACE_NARROW_CHROME_HEIGHT_PX + 8 : 8,
            left: narrow ? 8 : WORKSPACE_RAIL_WIDTH_PX + 8,
            right: 8,
            maxWidth: 480,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.5,
            background: incomingCallInfo ? 'rgba(255,243,224,0.98)' : 'rgba(255,255,255,0.96)',
            color: incomingCallInfo ? '#8a2f00' : '#333',
            border: incomingCallInfo ? '1px solid rgba(237,108,2,0.55)' : '1px solid rgba(0,0,0,0.08)',
          }}
        >
          {incomingCallInfo ? (
            <>
              <Typography variant="body2" sx={{ fontSize: '0.74rem', fontWeight: 700, flex: 1 }}>
                Incoming Call • From: {incomingCallInfo.from}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => acceptCall()}
                sx={{ minWidth: 72, height: 22, fontSize: '0.68rem', fontWeight: 700, borderRadius: '6px', py: 0, px: 1 }}
              >
                Answer
              </Button>
            </>
          ) : (
            <Box
              component="button"
              type="button"
              onClick={() => setCalendarOpen((prev) => !prev)}
              sx={{
                border: 0,
                background: 'transparent',
                color: '#333',
                cursor: 'pointer',
                fontSize: '0.75rem',
                textAlign: 'left',
                width: '100%',
                fontFamily: 'inherit',
              }}
            >
              {message}
            </Box>
          )}
        </Paper>
      ) : null}

      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          left: narrow ? 8 : WORKSPACE_RAIL_WIDTH_PX + 8,
          width: 320,
          top: narrow ? WORKSPACE_NARROW_CHROME_HEIGHT_PX + 8 : 8,
          transform: calendarOpen ? 'translateY(0)' : 'translateY(-12px)',
          opacity: calendarOpen ? 1 : 0,
          visibility: calendarOpen ? 'visible' : 'hidden',
          pointerEvents: calendarOpen ? 'auto' : 'none',
          transition: 'transform 220ms ease',
          zIndex: 1299,
          p: 1.25,
          background: 'rgba(255,255,255,0.96)',
          color: '#333',
          border: '1px solid rgba(0,0,0,0.08)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <IconButton
              size="small"
              aria-label="Previous month"
              onClick={() =>
                setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
              }
              sx={iconBtnSx}
            >
              <ChevronLeftIcon sx={{ fontSize: '18px' }} />
            </IconButton>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CalendarMonthIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                {monthTitle}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              aria-label="Next month"
              onClick={() =>
                setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
              }
              sx={iconBtnSx}
            >
              <ChevronRightIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
            {WEEKDAY_LABELS.map((label) => (
              <Typography
                key={label}
                variant="caption"
                sx={{ textAlign: 'center', color: 'rgba(0,0,0,0.55)', fontWeight: 600 }}
              >
                {label}
              </Typography>
            ))}

            {calendarCells.map((cell) => (
              <Box
                key={cell.key}
                sx={{
                  height: 24,
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  color: cell.day ? '#333' : 'transparent',
                  backgroundColor: cell.isToday ? 'rgba(56,142,60,0.15)' : 'transparent',
                  border: cell.isToday ? '1px solid rgba(56,142,60,0.45)' : '1px solid transparent',
                }}
              >
                {cell.day || '•'}
              </Box>
            ))}
          </Box>
        </Stack>
      </Paper>

      {actions.showPhone ? <CallPanel tenantId={tenantId} hideFab /> : null}
    </>
  );
}
